<template>
  <div class="residential-fields-price">
    <InputComponent
      v-model.number="price"
      :mask="{
        mask: Number,
        unmask: true,
        thousandsSeparator: ' ',
      }"
      :placeholder="$t('priceOnRequest')"
      :suffix="suffix"
      align="right"
      :label="label"
      :disabled="Boolean(internalDisabled)"
      v-tooltip.bottom="internalDisabled"
      class="residential-fields-price__value"
    >
      <template v-if="saleTypeComp === 'sale'" #below>
        <div class="row g-2 mt-1">
          <CheckboxComponent
            v-model="value.fixed"
            :disabled="Boolean(internalDisabled)"
            :label="$t('residentialSaleFixed')"
          />
          <CheckboxComponent
            v-if="!hasJointDebt"
            :value="hasJointDebt"
            @input="setHasJointDebt"
            :disabled="Boolean(internalDisabled)"
            :label="$t('hasJointDebt')"
          />
          <CheckboxComponent
            v-if="!hasDeposit"
            :value="hasDeposit"
            @input="setHasDeposit"
            :label="$t('deposit')"
            :disabled="Boolean(internalDisabled)"
          />
        </div>
      </template>
    </InputComponent>

    <InputComponent
      v-if="saleTypeComp === 'sale'"
      v-model.number="fee"
      :mask="{
        mask: Number,
        unmask: true,
        thousandsSeparator: ' ',
      }"
      align="right"
      :suffix="$store.state.enums.Region.currency"
      :disabled="Boolean(internalDisabled)"
      v-tooltip.bottom="internalDisabled"
      :label="$t('residentialSaleFee')"
    >
      <template #below>
        <CheckboxComponent
          v-model="value.fee.included"
          :label="$t('residentialSaleFeeIncluded')"
          class="mt-1"
          :disabled="Boolean(internalDisabled)"
        />
      </template>
    </InputComponent>
    <template v-if="hasJointDebt && saleTypeComp === 'sale'">
      <InputComponent
        v-model.number="jointDebt"
        :mask="{
          mask: Number,
          unmask: true,
          thousandsSeparator: ' ',
        }"
        align="right"
        :disabled="Boolean(internalDisabled)"
        v-tooltip.bottom="internalDisabled"
        :label="$t('jointDebt')"
        :suffix="$store.state.enums.Region.currency"
      >
        <template #below>
          <CheckboxComponent
            class="mt-1"
            :value="hasJointDebt"
            @input="setHasJointDebt"
            :label="$t('hasJointDebt')"
            :disabled="Boolean(internalDisabled)"
          />
        </template>
      </InputComponent>
      <InputComponent
        :mask="{
          mask: Number,
          unmask: true,
        }"
        v-model.number="jointDebtInterestRate"
        align="right"
        :disabled="Boolean(internalDisabled)"
        v-tooltip.bottom="internalDisabled"
        :label="$t('jointDebtInterestRate')"
        suffix="%"
        :max="100"
      >
      </InputComponent>
    </template>

    <InputComponent
      data-field="deposit"
      v-if="hasDeposit"
      v-model.number="deposit"
      :mask="{
        mask: Number,
        unmask: true,
        thousandsSeparator: ' ',
      }"
      align="right"
      :disabled="Boolean(internalDisabled)"
      v-tooltip.bottom="internalDisabled"
      :label="$t('shareOfDeposits')"
      :suffix="$store.state.enums.Region.currency"
    >
      <template #below>
        <CheckboxComponent
          class="mt-1"
          :value="hasDeposit"
          @input="setHasDeposit"
          :label="$t('deposit')"
          :disabled="Boolean(internalDisabled)"
        />
      </template>
    </InputComponent>
  </div>
</template>

<script>
import { ValueComp } from '@/utils'
import { NumberTransformer } from '@/utils/transformers'
import { InputComponent } from 'vue-elder-input'
import { CheckboxComponent } from 'vue-elder-checkbox'
import { i18n } from '@/i18n'

const meta = {
  order: 10,
  label: i18n.t('price'),
}

export { meta }
export default {
  props: {
    value: Object,
    disabled: Boolean,
    saleType: String,
    getDisabledField: {
      type: Function,
      default: () => false,
    },
  },
  watch: {
    'value.jointDebt': {
      handler(val) {
        if (this.hasJointDebt) return

        this.hasJointDebt = Number.isInteger(val)
      },
      immediate: true,
    },
    'value.deposit': {
      handler(val) {
        if (this.hasDeposit) return

        this.hasDeposit = Number.isInteger(val)
      },

      immediate: true,
    },
  },
  data() {
    return {
      hasJointDebt: false,
      hasDeposit: false,
    }
  },
  computed: {
    saleTypeComp() {
      return this.saleType || this.value.type
    },
    internalDisabled() {
      return this.disabled || this.getDisabledField('price')
    },
    comp: ValueComp(),
    label: () => meta.label,
    price: NumberTransformer('value.value'),
    fee: NumberTransformer('value.fee.value'),
    jointDebt: NumberTransformer('value.jointDebt'),
    jointDebtInterestRate: NumberTransformer('value.jointDebtInterestRate'),
    deposit: NumberTransformer('value.deposit'),
    suffix() {
      switch (this.saleTypeComp) {
        case 'sale':
          return this.$store.state.enums.Region.currency
        case 'rent':
          return `${this.$store.state.enums.Region.currency} / ${this.$t('multiplicatorMonthly')}`
      }
    },
  },
  methods: {
    setHasJointDebt(val) {
      this.hasJointDebt = val
      if (!val) this.value.jointDebt = null
    },
    setHasDeposit(val) {
      this.hasDeposit = val
      if (!val) this.value.deposit = null
    },
  },
  components: {
    InputComponent,
    CheckboxComponent,
  },
}
</script>

<style lang="scss">
.residential-fields-price {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: $form-gap;
  align-items: start;

  &:has(.residential-fields-price__value:last-child) {
    grid-template-columns: 1fr;
    grid-column-end: span 2;
  }
  [data-field='deposit'] {
    grid-column-end: span 2;
  }
}
</style>
